import { request } from '../../../services/requestService';

import getRestrictionsByEntity, { getRestrictionsByCodes } from '../utils/getRestrictionsByEntity';
import getSelectFieldsByEntity from '../utils/getSelectFieldsByEntity';
import getLimitByEntity from '../utils/getLimitByEntity';
import { SortDirection } from '../../../constants';
import { type IRestriction } from '../../../types';

import { type IFetchedData, type TRimmEntity } from '../data/rimmEntityTypes';
import { formatDateForBackend } from '../../../helpers/dateHelpers';
import { type RimmEntityType } from '../data/rimmEntityEnums';
import { RIMM_SERVER } from '../../../config';

export interface IFetchRimmEntityParams {
  entity: RimmEntityType;
  searchText?: string;
  sortByFields?: Record<string, SortDirection>;
  meta?: Record<string, unknown>;
  outerRimmServerURL?: string;
  offset?: number;
  signal?: AbortSignal;
  restrictions?: IRestriction[];
}

export const fetchRimmEntity = ({
  entity,
  outerRimmServerURL,
  sortByFields,
  meta,
  searchText = '',
  offset = 0,
  signal,
  restrictions,
}: IFetchRimmEntityParams): Promise<{
  data: IFetchedData;
}> => {
  const url = outerRimmServerURL ? outerRimmServerURL : `${entity}/search`;
  const baseURL = !outerRimmServerURL ? RIMM_SERVER : undefined;

  return request({
    method: 'POST',
    baseURL,
    url,
    data: {
      max: getLimitByEntity(entity),
      offset,
      distinct: false,
      restrictBy: 'OR',
      selectFields: getSelectFieldsByEntity(entity),
      restrictions: restrictions ?? getRestrictionsByEntity(entity, searchText),
      sortByFields: sortByFields ?? { code: SortDirection.ASC },
      meta: meta ?? { validityDate: formatDateForBackend(new Date()) },
    },
    signal,
  });
};

interface IFetchRimmEntityByCodeParams<T> {
  entity: T;
  code: string | number;
  outerRimmServerURL?: string;
  signal?: AbortSignal;
}

export const fetchRimmEntityByCode = <T extends RimmEntityType>({
  code,
  outerRimmServerURL,
  entity,
  signal,
}: IFetchRimmEntityByCodeParams<T>): Promise<{
  data: TRimmEntity[T];
}> => {
  const url = outerRimmServerURL ? outerRimmServerURL : `${entity}/${code}`;
  const baseURL = !outerRimmServerURL ? RIMM_SERVER : undefined;

  return request({
    method: 'GET',
    baseURL,
    url,
    signal,
  });
};

export interface IFetchRimmEntityByCodesParams {
  entity: RimmEntityType;
  codes: string[];
  sortByFields?: Record<string, SortDirection>;
  meta?: Record<string, unknown>;
  outerRimmServerURL?: string;
  signal?: AbortSignal;
}

export const fetchRimmEntityByCodes = ({
  entity,
  codes,
  meta,
  sortByFields,
  outerRimmServerURL,
  signal,
}: IFetchRimmEntityByCodesParams): Promise<{
  data: IFetchedData;
}> => {
  const url = outerRimmServerURL ? outerRimmServerURL : `${entity}/search`;
  const baseURL = !outerRimmServerURL ? RIMM_SERVER : undefined;

  return request({
    method: 'POST',
    baseURL,
    url,
    data: {
      max: codes.length,
      offset: 0,
      distinct: false,
      restrictBy: 'OR',
      selectFields: getSelectFieldsByEntity(entity),
      restrictions: getRestrictionsByCodes(codes),
      sortByFields: sortByFields ?? { code: SortDirection.ASC },
      meta: meta ?? { validityDate: formatDateForBackend(new Date()) },
    },
    signal,
  });
};
